import b from 'b_';
import {
    Button,
    Copy,
    LESSON_PASSPORT_TIP_THEME,
    LessonPassportButton,
    LessonPassportTip,
    SimpleCallback,
    Text,
    useIntl,
} from 'platform-components/common';
import { TEXT_SIZE } from 'platform-components/constants';
import { getHasAwards } from 'platform-components/src/components/awards/selectors';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { SPEC_COURSE_LESSON_MODE } from 'common.components/spec-course-lesson-view/typings';
import { getFormattedDate } from 'common.components/spec-course/utils';
import { useRegistrationLinkMetrika, useTeamRegistrationLink } from 'common.components/user/hooks';
import { getCurrentTeamLetter, getCurrentTeamLevel } from 'common.components/user/selectors';

import * as lessonSelectors from 'common.components/lesson/selectors';

import './lesson-view__passport-footer.scss';
import { getOlympsConfig } from 'platform-components/src/components/app-factory/selectors';
import * as courseSelectors from 'store/courses/selectors';
import { SPEC_COURSE_LESSON_STATUS } from 'common.components/spec-course/typings';
import { MODES_TO_SHOW_RESULTS } from '../../spec-course.store/constants';
import { SpecCourseLessonViewPassportButton } from '../__passport-button/lesson-view__passport-button';
import { getSpecCourseLessonStatus } from '../../spec-course.lessons-page/selectors';

interface Props {
    mode: SPEC_COURSE_LESSON_MODE;
    beginDate: Date | null;
    onResultsButtonClick: SimpleCallback;
    onAwardsButtonClick: SimpleCallback;
}

const getTheme = (mode: SPEC_COURSE_LESSON_MODE): LESSON_PASSPORT_TIP_THEME => {
    const LESSON_MODE_TO_TIP_THEME_MAPPER = {
        [SPEC_COURSE_LESSON_MODE.NO_STUDENTS]: LESSON_PASSPORT_TIP_THEME.YELLOW,
        [SPEC_COURSE_LESSON_MODE.NO_STUDENTS_FINISHED]: LESSON_PASSPORT_TIP_THEME.YELLOW,
        [SPEC_COURSE_LESSON_MODE.NOT_SOLVED]: LESSON_PASSPORT_TIP_THEME.YELLOW,
        [SPEC_COURSE_LESSON_MODE.NO_SCHOOL]: LESSON_PASSPORT_TIP_THEME.YELLOW,
        [SPEC_COURSE_LESSON_MODE.INVITE_STUDENTS]: LESSON_PASSPORT_TIP_THEME.BLUE,
        [SPEC_COURSE_LESSON_MODE.LESSON_ASSIGNED]: LESSON_PASSPORT_TIP_THEME.GREEN,
        [SPEC_COURSE_LESSON_MODE.LESSON_FINISHED]: LESSON_PASSPORT_TIP_THEME.GREEN,
        [SPEC_COURSE_LESSON_MODE.LESSON_IN_PROGRESS]: LESSON_PASSPORT_TIP_THEME.GREEN,
        [SPEC_COURSE_LESSON_MODE.CONTROL_WORK_ASSIGNED]: LESSON_PASSPORT_TIP_THEME.GREEN,
        [SPEC_COURSE_LESSON_MODE.CONTROL_WORK_FINISHED]: LESSON_PASSPORT_TIP_THEME.GREEN,
        [SPEC_COURSE_LESSON_MODE.CONTROL_WORK_IN_PROGRESS]: LESSON_PASSPORT_TIP_THEME.GREEN,
        [SPEC_COURSE_LESSON_MODE.WAITING_FOR_RESULTS]: LESSON_PASSPORT_TIP_THEME.GREEN,
    };

    return LESSON_MODE_TO_TIP_THEME_MAPPER[mode];
};

const getTipTextSize = (mode: SPEC_COURSE_LESSON_MODE): TEXT_SIZE => {
    if ([SPEC_COURSE_LESSON_MODE.INVITE_STUDENTS].includes(mode)) {
        return Text.SIZE.L;
    }

    return Text.SIZE.XL;
};

/** Текст с кнопкой внизу паспорта спецпроекта */
export const SpecCourseLessonViewPassportFooter = React.memo(
    // eslint-disable-next-line complexity
    ({ mode, beginDate, onAwardsButtonClick, onResultsButtonClick }: Props) => {
        const { classId } = useParams<{ classId: string }>();

        const intl = useIntl();

        const teamLevel = useSelector((store) => getCurrentTeamLevel(store, { classId }));
        const teamLetter = useSelector((store) => getCurrentTeamLetter(store, { classId }));

        const teamRegistrationLink = useTeamRegistrationLink();
        const { ymTeacherLinkCopy } = useRegistrationLinkMetrika();

        const shouldShowAwardsButton = useSelector(getHasAwards);
        const shouldShowResultButton = MODES_TO_SHOW_RESULTS.includes(mode);
        const shouldShowRegistrationLink = mode === SPEC_COURSE_LESSON_MODE.INVITE_STUDENTS;

        const tipTextSize = getTipTextSize(mode);
        const theme = getTheme(mode);
        const formattedDate = beginDate && getFormattedDate(beginDate, intl);

        const courses = useSelector((storeState) => courseSelectors.getCoursesByClass(storeState, { classId }));
        const isInformaticsTeacher = useMemo(() => {
            return courses.some((course) => course.subject === 'informatics' && course.date_closed === null);
        }, [courses]);

        const olympSlug = useSelector(lessonSelectors.getLessonSlug);
        const olympsConfig = useSelector(getOlympsConfig);
        const olympConfig = olympsConfig.olymps.find((olymp) => olympSlug === olymp.slug);

        const status = useSelector(getSpecCourseLessonStatus);

        // eslint-disable-next-line complexity
        const { isButtonPrepLessonAviable, propsButtonPrepLesson } = useMemo(() => {
            if (
                status === SPEC_COURSE_LESSON_STATUS.FINISHED ||
                mode === SPEC_COURSE_LESSON_MODE.NO_STUDENTS ||
                mode === SPEC_COURSE_LESSON_MODE.NO_STUDENTS_FINISHED ||
                mode === SPEC_COURSE_LESSON_MODE.NOT_SOLVED ||
                mode === SPEC_COURSE_LESSON_MODE.NO_SCHOOL ||
                mode === SPEC_COURSE_LESSON_MODE.INVITE_STUDENTS ||
                mode === SPEC_COURSE_LESSON_MODE.LESSON_ASSIGNED ||
                mode === SPEC_COURSE_LESSON_MODE.LESSON_IN_PROGRESS ||
                mode === SPEC_COURSE_LESSON_MODE.WAITING_FOR_RESULTS
            ) {
                return {
                    isButtonPrepLessonAviable: false,
                    propsButtonPrepLesson: undefined,
                };
            }

            const propsButton = isInformaticsTeacher
                ? olympConfig?.teacher?.buttonPrepLessonWithInformatic
                : olympConfig?.teacher?.buttonPrepLessonWithoutInformatic;

            const currentDate = Date.now();
            const isAvailableDateEnd = !propsButton?.endDate || currentDate < new Date(propsButton?.endDate).getTime();
            const isAvailableDateStart =
                !propsButton?.startDate || currentDate > new Date(propsButton?.startDate).getTime();
            return {
                isButtonPrepLessonAviable: Boolean(propsButton) && isAvailableDateStart && isAvailableDateEnd,
                propsButtonPrepLesson: propsButton,
            };
        }, [olympConfig, isInformaticsTeacher, status, mode]);

        return (
            <>
                {shouldShowAwardsButton && (
                    <LessonPassportButton
                        onClick={onAwardsButtonClick}
                        textId="specCourse.lessonView.passportTip.awardsButton"
                        view={Button.VIEW.ACTION}
                    />
                )}
                {shouldShowResultButton && (
                    <LessonPassportButton
                        mix="qa_spec-course-button_status-go_to_results"
                        onClick={onResultsButtonClick}
                        textId={'specCourse.LessonView.PassportTip.results_enabled.btn'}
                        view={Button.VIEW.SECONDARY}
                    />
                )}
                {!shouldShowResultButton && !isButtonPrepLessonAviable && (
                    <LessonPassportTip
                        mix={b('spec-course-lesson-passport', 'tip', { reduced: shouldShowRegistrationLink })}
                        theme={theme}
                    >
                        <Text size={tipTextSize} weight={Text.WEIGHT.M}>
                            {intl.formatMessage(
                                { id: `specCourse.LessonView.PassportTip.${mode}` },
                                { beginDate: formattedDate, teamLevel, teamLetter }
                            )}
                        </Text>
                        {shouldShowRegistrationLink && teamRegistrationLink && (
                            <Copy
                                className="spec-course-lesson-passport__registration-link-copy"
                                onClick={() => ymTeacherLinkCopy('olympiad', 'copy_link')}
                                text={teamRegistrationLink}
                            />
                        )}
                    </LessonPassportTip>
                )}
                {!shouldShowResultButton && isButtonPrepLessonAviable && (
                    <LessonPassportTip
                        mix={b('spec-course-lesson-passport', 'tip', { reduced: shouldShowRegistrationLink })}
                        theme={theme}
                    >
                        <Text size={tipTextSize} weight={Text.WEIGHT.M}>
                            {propsButtonPrepLesson?.beforeButtonText}
                        </Text>
                    </LessonPassportTip>
                )}
                <SpecCourseLessonViewPassportButton buttonPrepLessonProps={propsButtonPrepLesson} mode={mode} />
            </>
        );
    }
);
